<template>
  <div class="bulk-validation">
    <div class="bulk-validation__invalid-codes">
      <ElementAlert
        v-if="invalidCodes.length > 0"
        title="Unable to create styles with PLM codes:"
        type="error"
        :description="invalidCodes.join(', ')"
        show-icon
      />

      <ElementAlert
        v-if="duplicatedCodes.length > 0"
        :title="`The following PLM codes already exist in Season ${season.name}:`"
        type="warning"
        :description="duplicatedCodes.join(', ')"
        show-icon
      />

      <ElementAlert
        v-if="deadlineError.show"
        type="error"
        title="Submission deadline has to be set."
        show-icon
        class="bulk-validation__deadline-error"
      />
    </div>
    <ElementTable
      :data="displayPLMData"
      border
      stripe
      class="bulk-validation__table"
      :cell-style="{ height: '55px' }"
      empty-text="Data not found."
    >
      <ElementTableColumn
        label="PLM Code"
        prop="plmCode"
      />

      <ElementTableColumn
        label="Season"
        prop="season"
      >
        <template slot-scope="scope">
          {{ scope.row.season.name }}
        </template>
      </ElementTableColumn>

      <ElementTableColumn
        :key="'vendor-col-' + vendorsKey"
        label="Vendor Company"
        prop="vendorCompany"
        width="210"
      >
        <template slot="header">
          <div class="bulk-validation__header bulk-validation__header--short">
            <span class="bulk-validation__label">Vendor<br>Company</span>
            <ElementSelect
              v-model="headerVendorCompany"
              placeholder="Set all to"
              size="mini"
              filterable
              clearable
              class="bulk-validation__set-all"
            >
              <ElementOption
                v-for="vendor in vendors"
                :key="vendor.id"
                :label="vendor.name"
                :value="vendor.id"
              />
            </ElementSelect>
          </div>
        </template>
        <template slot-scope="scope">
          <ElementSelect
            v-model="scope.row.vendorCompany"
            size="mini"
            filterable
            clearable
            @clear="scope.row.vendorCompany = null"
          >
            <ElementOption
              v-for="vendor in vendors"
              :key="`${vendor.name}`"
              :label="vendor.name"
              :value="vendor.id"
            />
          </ElementSelect>
        </template>
      </ElementTableColumn>

      <ElementTableColumn
        :key="'rpu-col-' + rpuKey"
        label="Responsible Product"
        prop="responsibleProduct"
        width="220"
      >
        <template slot="header">
          <div class="bulk-validation__header bulk-validation__header--short">
            <span class="bulk-validation__label">Responsible<br>Product</span>
            <ElementSelect
              v-model="headerResponsibleProduct"
              placeholder="Set all to"
              filterable
              size="mini"
              clearable
              class="bulk-validation__set-all"
            >
              <ElementOption
                v-for="u in responsibleProductUsers"
                :key="u.id"
                :label="u.email"
                :value="u.id"
              />
            </ElementSelect>
          </div>
        </template>
        <template slot-scope="scope">
          <ElementSelect
            v-model="scope.row.responsibleProduct"
            size="mini"
            filterable
            clearable
            @clear="scope.row.responsibleProduct = null"
          >
            <ElementOption
              v-for="responsibleProductUser in responsibleProductUsers"
              :key="responsibleProductUser.email"
              :label="responsibleProductUser.email"
              :value="responsibleProductUser.id"
            />
          </ElementSelect>
        </template>
      </ElementTableColumn>

      <ElementTableColumn
        label="Submission Deadline"
        prop="submissionDeadline"
        width="240"
      >
        <template slot="header">
          <div class="bulk-validation__submission-deadline">
            <span class="bulk-validation__label">Submission<br>Deadline</span>
            <ElementDatePicker
              v-model="headerSubmissionDeadline"
              type="date"
              placeholder="Set all to"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              :clearable="true"
              :picker-options="datePickerOptions"
              :class="{ 'is-error': deadlineError.header }"
            />
          </div>
        </template>
        <template slot-scope="scope">
          <ElementDatePicker
            v-model="scope.row.submissionDeadline"
            type="date"
            placeholder="Select a date"
            :picker-options="datePickerOptions"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :clearable="true"
            :class="{ 'is-error': deadlineError.rows[scope.row.plmCode] }"
          />
        </template>
      </ElementTableColumn>

      <ElementTableColumn
        label="Status"
        prop="status"
      >
        <template slot="header">
          <div class="bulk-validation__status-checkbox">
            Status
            <ElementCheckbox @change="setStatus">
              Set all to Requested
            </ElementCheckbox>
          </div>
        </template>
        <template slot-scope="scope">
          <ElementSelect
            v-model="scope.row.status"
            size="mini"
          >
            <ElementOption
              v-for="status in [ASSET_STATUS.CREATED, ASSET_STATUS.REQUESTED]"
              :key="`${status.action}`"
              :label="status.action"
              :value="status.value"
            />
          </ElementSelect>
        </template>
      </ElementTableColumn>
    </ElementTable>

    <ElementPagination
      v-if="plmData.length > 0"
      align="right"
      layout="prev, pager, next"
      :total="plmData.length"
      :page-size="pageSize"
      :current-page="currentPage"
      @current-change="setPage"
    />

    <ElementButton
      type="primary"
      class="bulk-validation__back-button"
      icon="el-icon-arrow-left"
      plain
      @click="backToCreation"
    >
      Previous Step
    </ElementButton>
    <ElementButton
      type="danger"
      class="bulk-validation__back-button"
      icon="el-icon-close"
      plain
      @click="backToHome"
    >
      Cancel
    </ElementButton>
    <ElementButton
      type="primary"
      class="bulk-validation__creation-button"
      :loading="isLoading"
      :disabled="plmData.length === 0"
      @click="createStyles"
    >
      Create Styles
    </ElementButton>
  </div>
</template>

<script>
import VueTypes from 'vue-types'
import { ASSET_STATUS } from '@/constants/assetStatuses'
import { mapActions } from 'vuex'

export default {
  name: 'StyleBulkValidation',

  props: {
    validCodes: VueTypes.array,
    invalidCodes: VueTypes.array,
    duplicatedCodes: VueTypes.array,
    season: VueTypes.object,
    vendors: VueTypes.array.def([]),
    responsibleProductUsers: VueTypes.array.def([])
  },

  data () {
    return {
      isLoading: false,
      ASSET_STATUS,
      plmData: [],
      currentPage: 1,
      pageSize: 10,
      headerVendorCompany: null,
      headerResponsibleProduct: null,
      headerSubmissionDeadline: null,
      deadlineError: {
        show: false,
        header: false,
        rows: {}
      },
      datePickerOptions: {
        /**
         * Disable any date before tomorrow.
         *
         * @param   {Date}    date - The date being evaluated by the picker.
         * @returns {boolean}      - `true` if the date should be disabled (i.e. is before tomorrow).
         */
        disabledDate (date) {
          const today = new Date()
          const tomorrow = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() + 1
          )

          return date < tomorrow
        }
      }
    }
  },

  computed: {
    /**
     * Cache key for the Vendor header.
     *
     * @returns {string}
     */
    vendorsKey () {
      return this.vendors && this.vendors.length
        ? this.vendors.map(v => v.id).join('|')
        : 'empty'
    },

    /**
     * Cache key for the Responsible Product header.
     *
     * @returns {string}
     */
    rpuKey () {
      return this.responsibleProductUsers && this.responsibleProductUsers.length
        ? this.responsibleProductUsers.map(u => u.id).join('|')
        : 'empty'
    },

    /**
     * @returns { Array }
     */
    displayPLMData () {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize

      return this.plmData.slice(start, end)
    }
  },

  watch: {
    /**
     *
     */
    validCodes () {
      this.initPlmData()
    },

    /**
     * Watcher for the header vendor company.
     *
     * Whenever the header "Set all to" picker changes,
     * propagate its value to all rows in `plmData`.
     * If the value is cleared, rows keep their existing values.
     *
     * @param {string|null} val - New string or `null` if cleared.
     */
    headerVendorCompany (val) {
      if (!val) {
        return
      }

      this.plmData = this.plmData.map(row => ({
        ...row,
        vendorCompany: val
      }))
    },

    /**
     * Watcher for the header responsible product.
     *
     * Whenever the header "Set all to" picker changes,
     * propagate its value to all rows in `plmData`.
     * If the value is cleared, rows keep their existing values.
     *
     * @param {string|null} val - New string or `null` if cleared.
     */
    headerResponsibleProduct (val) {
      if (!val) {
        return
      }

      this.plmData = this.plmData.map(row => ({
        ...row,
        responsibleProduct: val
      }))
    },

    /**
     * Watcher for the header submission deadline.
     *
     * Whenever the header "Set all to date" date picker changes,
     * propagate its value to all rows in `plmData`.
     * If the value is cleared, rows keep their existing dates.
     *
     * @param {string|null} val - New date string in `yyyy-MM-dd` format,
     *                          or `null` if cleared.
     */
    headerSubmissionDeadline (val) {
      if (!val) {
        return
      }

      this.plmData = this.plmData.map(row => ({
        ...row,
        submissionDeadline: val
      }))

      // Clear any deadline error flags
      this.deadlineError.header = false
      this.deadlineError.rows = {}
    }
  },

  methods: {
    ...mapActions(['stylesBulkCreation']),

    /**
     * @param {number} page
     */
    setPage (page) {
      this.currentPage = page
    },

    /**
     * @param {boolean} value
     */
    setStatus (value) {
      const status = value
        ? ASSET_STATUS.REQUESTED.value
        : ASSET_STATUS.CREATED.value

      this.plmData = this.plmData.map(code => {
        return {
          ...code,
          status: status
        }
      })
    },

    /**
     */
    async createStyles () {
      if (!this.validateDeadlines()) {
        return
      }

      this.isLoading = true
      const styles = await this.stylesBulkCreation({
        groupId: this.$route.query.group_id,
        data: this.processPlmData(this.plmData)
      })
      this.isLoading = false
      this.$emit('set-created-styles', { styles })
    },

    /**
     */
    backToCreation () {
      this.$emit('back-to-previous-step')
    },

    /**
     */
    backToHome () {
      this.$emit('back-to-homepage')
    },

    /**
     */
    initPlmData () {
      this.plmData = this.validCodes.map(code => {
        return {
          plmCode: code,
          status: ASSET_STATUS.CREATED.value,
          vendorCompany: null,
          responsibleProduct: null,
          season: this.season,
          submissionDeadline: null
        }
      })
    },

    /**
     * @param   {Array}    data
     *
     * @returns {object[]}
     */
    processPlmData (data) {
      return data.map(item => {
        return {
          plm_code: item.plmCode,
          season_id: item.season.id,
          status: item.status,
          vendor_company_id: item.vendorCompany,
          responsible_product_id: item.responsibleProduct,
          submission_deadline: item.submissionDeadline
        }
      })
    },

    /**
     * @returns {boolean}
     */
    validateDeadlines () {
      this.deadlineError.show = false
      this.deadlineError.header = false
      this.deadlineError.rows = {}

      const rowsAllHaveValues = this.plmData.every(r => !!r.submissionDeadline)

      if (!rowsAllHaveValues) {
        // Mark each empty row as error
        this.plmData.forEach(r => {
          if (!r.submissionDeadline) {
            this.deadlineError.rows[r.plmCode] = true
          }
        })

        // Also show header as error if it’s empty
        if (!this.headerSubmissionDeadline) {
          this.deadlineError.header = true
        }

        this.deadlineError.show = true

        return false
      }

      return true
    }
  }
}
</script>

<style lang="scss" scoped>
.bulk-validation {
  margin: spacing(2) 0 spacing(2) 0;
}

.bulk-validation__table {
  margin-bottom: spacing(2);
}

.bulk-validation__creation-button {
  float: right;
  margin-bottom: spacing(2);
}

.bulk-validation__back-button {
  float: left;
  margin-bottom: spacing(2);
}

.bulk-validation__invalid-codes {
  margin-bottom: spacing(2);
}

.bulk-validation__invalid-codes p {
  color: $red;
  font-size: spacing(1.5);
}

.bulk-validation__invalid-codes .el-alert {
  margin-bottom: spacing(0.5);
}
</style>

<style lang="scss">
$header-font-size: 14px;
$header-font-weight: 600;
$header-height: 32px;
$header-line-height: 32px;
$header-max-width: 95px;
$header-gap: 0;
$header-flex: 150px;
$header-set-all-font-size: 11px;
$header-flex-set-all: 200px;
$submission-deadline-max-width: 120px;

.bulk-validation__submission-deadline {
  display: flex;
  gap: $header-gap;
  align-items: center;
  justify-content: space-between;
  padding: 0 spacing(1);
}

.bulk-validation__submission-deadline .el-date-editor {
  flex: 0 0 $header-flex;
  max-width: $submission-deadline-max-width;
  margin-left: auto;
}

.bulk-validation__submission-deadline .el-input__inner {
  height: $header-height;
  font-size: $header-set-all-font-size;
  line-height: $header-line-height;
}

.el-date-editor.is-error .el-input__inner {
  border-color: $red !important;
}

.bulk-validation__deadline-error {
  margin-bottom: spacing(1);
}

.el-table .cell .el-select,
.el-table .cell .el-date-editor,
.el-table .cell .el-input {
  display: block;
  box-sizing: border-box;
  width: 100% !important;
}

.bulk-validation__status-checkbox .el-checkbox {
  float: inline-end;
}

.bulk-validation__status-checkbox .el-checkbox__label {
  padding-left: spacing(1);
  font-size: $font-size-label;
}

.bulk-validation__header {
  display: flex;
  gap: $header-gap;
  align-items: center;
  justify-content: space-between;
  padding: 0 spacing(1);
}

.bulk-validation__header .bulk-validation__label {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: $header-font-weight;
  font-size: $header-font-size;
  line-height: 1.2;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
}

.bulk-validation__header .bulk-validation__set-all {
  flex: 0 0 $header-flex;
  max-width: $header-max-width;
  margin-left: auto;

  .el-input__inner {
    height: $header-height;
    font-size: $header-set-all-font-size;
    line-height: $header-line-height;
  }
}

.bulk-validation__header--short {
  gap: 0;

  .bulk-validation__set-all {
    flex: 0 0 $header-flex-set-all;
    max-width: $header-max-width;
  }
}

/* stylelint-disable selector-max-compound-selectors */
.bulk-validation .el-select .el-input .el-select__caret {
  color: $grey-very-light;
  font-weight: $font-weight-bold;
}
</style>
